<template>
  <div class="atb-divide-y atb-divide-gray-300 atb-py-6">
    <Card class="card-size atb-ml-20 atb-pt-6">
      <template #body>
        <Form>
          <div
            class="atb-grid atb-grid-cols-2 atb-gap-y-4 atb-gap-x-14 atb-px-2 atb-py-4"
          >
            <div>
              <FormField
                :label="$t('Username')"
                required="true"
                form-field-class="input-field-width"
                :message="usernameErrorMsg"
                :message-type="'error'"
              >
                <Textbox
                  id="usernameTextbox"
                  v-model="userDataValue.login"
                  type="text"
                  :state="usernameErrorMsg ? 'error' : 'basic'"
                  :disabled="isUsernameReadOnly"
                  @update:modelValue="usernameErrorMsg = ''"
                />
              </FormField>
            </div>
            <div>
              <FormField
                :label="$t('email')"
                required="true"
                form-field-class="input-field-width"
                :message="emailErrorMsg"
                :message-type="'error'"
              >
                <Textbox
                  id="emailTextbox"
                  v-model="userDataValue.emailAddress"
                  type="email"
                  :state="emailErrorMsg ? 'error' : 'basic'"
                  @update:modelValue="emailErrorMsg = ''"
                />
              </FormField>
            </div>
            <div>
              <FormField
                :label="$t('firstName')"
                required="true"
                form-field-class="input-field-width"
                :message="firstNameErrorMsg"
                :message-type="'error'"
              >
                <Textbox
                  id="firstNameTextbox"
                  v-model="userDataValue.firstName"
                  type="text"
                  :state="firstNameErrorMsg ? 'error' : 'basic'"
                  @update:modelValue="firstNameErrorMsg = ''"
                />
              </FormField>
            </div>
            <div>
              <FormField
                :label="$t('lastName')"
                required="true"
                form-field-class="input-field-width"
                :message="lastNameErrorMsg"
                :message-type="'error'"
              >
                <Textbox
                  id="lastNameTextbox"
                  v-model="userDataValue.lastName"
                  type="text"
                  :state="lastNameErrorMsg ? 'error' : 'basic'"
                  @update:modelValue="lastNameErrorMsg = ''"
                />
              </FormField>
            </div>
            <div>
              <FormField
                :label="$t('password')"
                :required="!isEditMode"
                form-field-class="input-field-width"
                :message="passwordErrorMsg"
                :message-type="'error'"
              >
                <Textbox
                  id="passwordTextbox"
                  v-model="userDataValue.password"
                  input-type="password"
                  :state="passwordErrorMsg ? 'error' : 'basic'"
                  @update:modelValue="passwordErrorMsg = ''"
                />
              </FormField>
            </div>
            <div>
              <FormField
                :label="$t('confirmPassword')"
                :required="!isEditMode"
                form-field-class="input-field-width"
                :message="confirmPasswordErrorMsg"
                :message-type="'error'"
              >
                <Textbox
                  id="confirmPasswordTextbox"
                  v-model="userDataValue.confirmPassword"
                  input-type="password"
                  :state="confirmPasswordErrorMsg ? 'error' : 'basic'"
                  @update:modelValue="confirmPasswordErrorMsg = ''"
                />
              </FormField>
            </div>
            <div>
              <FormField form-field-class="input-field-width">
                <Checkbox
                  id="isSuperUserCheckbox"
                  v-model:checked="userDataValue.superUser"
                  :label="$t('superUser')"
                />
              </FormField>
            </div>
            <div>
              <FormField form-field-class="input-field-width">
                <Checkbox
                  id="userMayChangePasswordCheckbox"
                  v-model:checked="userDataValue.canChangePassword"
                  :label="$t('canChangePassword')"
                />
              </FormField>
            </div>
            <div>
              <FormField form-field-class="input-field-width">
                <Checkbox
                  id="changePasswordAtNextLoginCheckbox"
                  v-model:checked="userDataValue.forcePasswordChange"
                  :label="$t('forcePasswordChange')"
                />
              </FormField>
            </div>
            <div class="atb-col-span-2">
              <FormField
                :label="$t('notes')"
                form-field-class="input-field-width"
              >
                <Textarea
                  id="userNotesTextarea"
                  v-model="userDataValue.notes"
                  field-control-class="atb-h-24"
                />
              </FormField>
            </div>
          </div>
        </Form>

        <div class="role-table atb-m-4 atb-h-100">
          <div
            class="atb-font-bold atb-px-4 atb-py-4 atb-flex atb-align-center"
          >
            {{ $t('Roles') }}
          </div>
          <div class="grid-top-border">
            <AgGridVue
              :style="{ height: '350px' }"
              class="ag-theme-eds atb-w-full atb-h-full"
              :column-defs="roleColumnDefs"
              :row-data="availableRoles"
              :row-selection="'multiple'"
              :suppress-row-click-selection="true"
              :pagination="false"
              :row-multi-select-with-click="true"
              @grid-ready="onRolesGridReady"
              @row-selected="onRoleRowSelected"
            />
          </div>
        </div>

        <div class="atb-flex atb-flex-row atb-justify-end atb-gap-x-8 atb-pt-8">
          <Button
            id="overviewTabDeleteButton"
            type="submit"
            motif="basic"
            :label="$t('delete')"
            :title="$t('delete')"
            :disabled="readOnly || isDeleteDisabled"
            @click="deleteUser"
          />
          <Button
            id="overviewTabSaveButton"
            type="submit"
            motif="primary"
            :label="$t('save')"
            :title="$t('save')"
            :disabled="readOnly"
            @click="saveUser"
          />
        </div>
      </template>
    </Card>
  </div>
</template>
<script lang="ts" setup>
  import { ref, onMounted, computed, toRefs, Ref, watch, nextTick } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useI18n } from 'vue-i18n';
  import Card from '@atb-ui/eds-vue/src/components/Card.vue';
  import Form from '@atb-ui/eds-vue/src/components/Form/Form.vue';
  import FormField from '@atb-ui/eds-vue/src/components/Form/FormField.vue';
  import Textbox from '@atb-ui/eds-vue/src/components/Textbox.vue';
  import Textarea from '@atb-ui/eds-vue/src/components/Textarea.vue';
  import Checkbox from '@atb-ui/eds-vue/src/components/Checkbox.vue';
  import Button from '@atb-ui/eds-vue/src/components/Button.vue';
  import { AgGridVue } from 'ag-grid-vue3/lib/AgGridVue';
  import { useRoleStore } from '@/app/features/admin/roles/role-store';
  import { useAlertStore } from '@/app/shared/stores/alert-store';
  import { useAuthStore } from '@/app/shared/stores/auth-store';
  import { Motif } from '@/app/shared/enums/motif-enum';
  import { UserData } from '../user-data.interface';
  import { AgGridEvent, GridApi } from 'ag-grid-community';
  import { Role } from '@/app/shared/types/models/role.interface';

  const { t } = useI18n();
  const roleStore = useRoleStore();
  const alertStore = useAlertStore();
  const authStore = useAuthStore();
  const { roleList } = storeToRefs(roleStore);

  interface UserTabEvent {
    (_event: 'saveUserData', _params: UserData): void;
    (_event: 'deleteUserData', _params: number | string): void;
  }

  interface UserTabProps {
    userData: UserData;
    readOnly: boolean;
  }

  const props = defineProps<UserTabProps>();
  const emit = defineEmits<UserTabEvent>();
  const { userData, readOnly } = toRefs(props);
  const dsGridApi = ref(null) as Ref<GridApi | null>;
  const selectedRoles: Ref<any[]> = ref([]);
  const userDataValue = userData.value;

  const isEditMode = computed(() => userData.value?.id !== -1);
  const isDeleteDisabled = computed(() => userData.value?.id === -1);
  const isUsernameReadOnly = computed(
    () => isEditMode.value && authStore.shouldUseOktaLogin
  );
  const usernameErrorMsg = ref('');
  const emailErrorMsg = ref('');
  const firstNameErrorMsg = ref('');
  const lastNameErrorMsg = ref('');
  const passwordErrorMsg = ref('');
  const confirmPasswordErrorMsg = ref('');

  const availableRoles = computed(() => {
    return roleList.value?.content || [];
  });

  onMounted(async () => {
    await roleStore.fetchRoles();
  });

  watch(availableRoles, (newRoles) => {
    if (dsGridApi.value && newRoles.length > 0) {
      dsGridApi.value.setRowData(newRoles);
      nextTick(() => {
        updateSelectedRoles();
        dsGridApi.value?.sizeColumnsToFit();
      });
    }
  });

  const roleColumnDefs = ref([
    {
      field: 'check',
      headerName: '',
      width: 56,
      suppressSizeToFit: true,
      headerCheckboxSelection: true,
      checkboxSelection: true,
    },
    {
      field: 'name',
      flex: 1,
      headerName: t('roleName'),
      suppressSizeToFit: true,
      sortable: true,
    },
  ]);

  const onRolesGridReady = (params: AgGridEvent) => {
    dsGridApi.value = params.api;
    if (availableRoles.value.length > 0) {
      dsGridApi.value.setRowData(availableRoles.value);
      nextTick(() => {
        updateSelectedRoles();
        dsGridApi.value?.sizeColumnsToFit();
      });
    }
  };

  const updateSelectedRoles = () => {
    const selectedRolesIds =
      userData.value.roles?.map((elem: Role) => elem.id) || [];
    if (selectedRolesIds.length === 0) {
      return;
    }
    dsGridApi.value?.forEachNode((node) => {
      const isSelected = selectedRolesIds.includes(node.data.id);
      node.setSelected(isSelected);
    });
  };

  const onRoleRowSelected = () => {
    const selectedData = dsGridApi.value?.getSelectedNodes();
    selectedRoles.value = selectedData?.map((node) => node.data.name) || [];
  };

  const validateForm = (): boolean => {
    usernameErrorMsg.value = '';
    emailErrorMsg.value = '';
    firstNameErrorMsg.value = '';
    lastNameErrorMsg.value = '';
    passwordErrorMsg.value = '';
    confirmPasswordErrorMsg.value = '';

    let hasError = false;

    if (!userDataValue.login) {
      usernameErrorMsg.value = t('emptyField');
      hasError = true;
    }
    if (!userDataValue.emailAddress) {
      emailErrorMsg.value = t('emptyField');
      hasError = true;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(userDataValue.emailAddress)) {
        emailErrorMsg.value = t('invalidEmailFormat');
        hasError = true;
      }
    }
    if (!userDataValue.firstName) {
      firstNameErrorMsg.value = t('emptyField');
      hasError = true;
    }
    if (!userDataValue.lastName) {
      lastNameErrorMsg.value = t('emptyField');
      hasError = true;
    }

    if (!isEditMode.value || userDataValue.password) {
      if (!userDataValue.password) {
        passwordErrorMsg.value = t('emptyField');
        hasError = true;
      } else if (userDataValue.password.length < 6) {
        passwordErrorMsg.value = t('passwordMinLength');
        hasError = true;
      }

      if (userDataValue.password !== userDataValue.confirmPassword) {
        confirmPasswordErrorMsg.value = t('passwordsDoNotMatch');
        hasError = true;
      }
    }

    return !hasError;
  };

  const saveUser = () => {
    if (!validateForm()) {
      return;
    }

    const selectedRoleList: Role[] | undefined =
      dsGridApi.value?.getSelectedRows();

    userDataValue.roles = selectedRoleList || [];

    const userDataToSave = { ...userDataValue };
    if (!userDataToSave.password) {
      delete userDataToSave.password;
      delete userDataToSave.confirmPassword;
    }

    emit('saveUserData', userDataToSave);

    alertStore.showNotification(
      t('userSavedSuccessfully'),
      t('user'),
      Motif.SUCCESS
    );
  };

  const deleteUser = () => {
    const userId = userData.value.id;
    if (userId != null) {
      emit('deleteUserData', userId);
    } else {
      console.error('Cannot delete: user ID is missing.');
    }
  };
</script>
<style scoped>
  .card-size {
    width: 70%;
    height: 100%;
  }

  .input-field-width {
    width: 100%;
  }

  .option-class {
    text-align: left;
    padding: 10px 16px;
  }

  .d-active {
    background-color: #426da9;
    color: white;
  }

  .card-title {
    font-weight: 500;
    font-size: 16px;
    color: #767676;
  }

  .tools-header-tab {
    align-items: center;
    height: 50px;
  }

  .role-table {
    border: 1px solid #d6d6d6;
    border-radius: 6px;
  }

  .grid-top-border {
    border-top: 1px solid #d6d6d6;
  }
</style>
